import type { SeriesRef } from '../dicomweb';
import type { LoaderContext } from '../loader';
import { JPEG_FRAME_SCHEME, jpegFrameImageId, renderedFrameImageId } from '../jpegframes';

/**
 * "Resolución de referencia" (spec section 4, `Fy_useDicomFormat`): a series whose stored transfer
 * syntax the browser cannot decode is shown as server-rendered 8-bit JPEG frames instead, labelled on
 * the image, with W/L presets disabled. IMEXHS decides by the series' transfer syntaxes; this viewer
 * does the same (AvailableTransferSyntaxUID in the WADO-RS metadata, which dcm4chee provides) and also
 * falls back when a decode fails anyway.
 */

/** IMEXHS's DICOM-capable set plus what the pinned WADO loader 4.13.2 also decodes. */
export const DECODABLE_TRANSFER_SYNTAXES = new Set([
  '1.2.840.10008.1.2', '1.2.840.10008.1.2.1', '1.2.840.10008.1.2.2', '1.2.840.10008.1.2.5',
  '1.2.840.10008.1.2.4.50', '1.2.840.10008.1.2.4.51', '1.2.840.10008.1.2.4.57', '1.2.840.10008.1.2.4.70',
  '1.2.840.10008.1.2.4.80', '1.2.840.10008.1.2.4.81', '1.2.840.10008.1.2.4.90', '1.2.840.10008.1.2.4.91',
  // Decoded by the pinned 4.13.2 loader, not listed by IMEXHS:
  '1.2.840.10008.1.2.1.99', '3.2.840.10008.1.2.4.96',
  '1.2.840.10008.1.2.4.201', '1.2.840.10008.1.2.4.202', '1.2.840.10008.1.2.4.203',
]);

export const PRESETS_UNSUPPORTED = ' Presets no compatibles con esta resolución, por favor cambiar a la resolución completa. ';
const AVAILABLE_TRANSFER_SYNTAX = '00083002';

/** True when the metadata names a transfer syntax for some instance and none of them can be decoded here. */
export function seriesNeedsReference(series: SeriesRef): boolean {
  let known = false;
  for (const instance of series.instances) {
    const values = instance.metadata?.[AVAILABLE_TRANSFER_SYNTAX]?.Value as string[] | undefined;
    if (!values?.length) continue;
    known = true;
    if (values.some((uid) => DECODABLE_TRANSFER_SYNTAXES.has(uid))) return false;
  }
  return known;
}

/** One reference (JPEG) imageId per frame, in display order. */
export function referenceImageIds(ctx: LoaderContext, series: SeriesRef): string[] {
  const ids: string[] = [];
  for (const instance of series.instances) {
    for (let frame = 1; frame <= instance.numberOfFrames; frame++) {
      ids.push(ctx.mode === 'fast'
        ? renderedFrameImageId(ctx, series, instance, frame)
        : jpegFrameImageId(ctx, series, instance, frame));
    }
  }
  return ids;
}

/** An 8-bit JPEG reference image (also parity-mode multi-frame, which IMEXHS always shows as JPEG). */
export function isReferenceImageId(imageId: string | undefined): boolean {
  return imageId?.startsWith(`${JPEG_FRAME_SCHEME}:`) ?? false;
}
